import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import "./Type.css";
// import { fetchAppointmentTypes } from "src/redux/slices/appointmentTypeSlice"; // adjust path
import ReactApexChart from "react-apexcharts";
import { fetchAppointmentTypes } from "../../../../redux/reducers/appointmentTypeSlice";
import { minutesToHours } from "../../../../util/function";


function AppointType() {
  const dispatch = useDispatch();
  const { typeOfAppointment, reschedulingRate, patientWaitTime, loading, enhancedAppointmentTypeMix, enhancedNoShowRescheduleRate } =
    useSelector((state) => state.appointmentTypes);
  const { dashboardData } = useSelector((state) => state.dashboard);
  const [selectedSpeciesAge, setSelectedSpeciesAge] = useState("Dog");
  const [view, setView] = useState("Weekly");
  const [donutSeries, setDonutSeries] = useState([]);
  const [donutLabels, setDonutLabels] = useState([]);
  const [barData, setBarData] = useState([]);
  const [boxPlotSeries, setBoxPlotSeries] = useState([]);
  const [noShowCategories, setNoShowCategories] = useState([]);

  useEffect(() => {
    dispatch(fetchAppointmentTypes());
  }, [dispatch]);

  useEffect(() => {
    if (loading) return;
    const categories = enhancedAppointmentTypeMix?.serviceTypes?.map((li) => li?.serviceType) || [];
    const values = enhancedAppointmentTypeMix?.serviceTypes?.map((li) => li?.count ?? 0) || [];
    setDonutLabels(categories);
    setDonutSeries(values);

    // Bar Chart Data - only use real data from reschedulingRate
    if (Array.isArray(reschedulingRate) && reschedulingRate?.length > 0) {
      setBarData(reschedulingRate);
    } else {
      setBarData([]); // Clear data if none available
    }

    // Box Plot Data - only use real data from patientWaitTime
    const waitTime2025 = patientWaitTime?.["2025"] || [];
    if (Array.isArray(waitTime2025) && waitTime2025.length > 0) {
      const formattedBoxData = waitTime2025.map((item) => ({
        x: item?.month || '',
        y: [
          item?.min ?? 0,
          item?.q1 ?? 0,
          item?.median ?? 0,
          item?.q3 ?? 0,
          item?.max ?? 0
        ],
      }));
      setBoxPlotSeries([
        {
          type: "boxPlot",
          data: formattedBoxData,
        },
      ]);
    } else {
      setBoxPlotSeries([]); // Clear data if none available
    }
  }, [typeOfAppointment, reschedulingRate, patientWaitTime, loading]);

  useEffect(() => {
    const reqCategories = enhancedNoShowRescheduleRate?.trendData?.map((li) => li?.period) || [];
    setNoShowCategories(reqCategories || []);
  }, [enhancedNoShowRescheduleRate])

  const ageSeries = useMemo(() => {
    if (!enhancedNoShowRescheduleRate?.trendData || !Array.isArray(enhancedNoShowRescheduleRate.trendData)) return [];
    const trendData = enhancedNoShowRescheduleRate.trendData;
    const noShowData = trendData.map((item) => Number(item?.noShowCount ?? 0)) || [];
    const rescheduleData = trendData.map((item) => Number(item?.rescheduleCount ?? 0)) || [];

    // Ensure both arrays have the same length
    const maxLength = Math.max(noShowData.length, rescheduleData.length);
    const paddedNoShow = [...noShowData, ...Array(maxLength - noShowData.length).fill(0)];
    const paddedReschedule = [...rescheduleData, ...Array(maxLength - rescheduleData.length).fill(0)];

    return [
      { name: "No Show", data: paddedNoShow },
      { name: "Reschedule", data: paddedReschedule }
    ];
  }, [enhancedNoShowRescheduleRate]);

  const donutOptions = {
    chart: { type: "donut" },
    labels: donutLabels || [],
    colors: [
      "#1D4ED8",
      "#9333EA",
      "#8B5CF6",
      "#06B6D4",
      "#A78BFA",
      "#EC4899",
      "#FBBF24",
      "#34D399",
      "#1D4ED8",
      "#9333EA",
      "#8B5CF6",
      "#06B6D4",
      "#A78BFA",
      "#EC4899",
      "#FBBF24",
      "#34D399",
    ],
    legend: { show: false },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${Math.round(val)}%`,
    },
    plotOptions: {
      pie: {
        donut: { size: "70%" },
      },
    },
    tooltip: {
      custom: function ({ series, seriesIndex, w }) {
        try {
          const label = w?.globals?.labels?.[seriesIndex] || 'Unknown';
          const value = series?.[seriesIndex] ?? 0;
          const total = Array.isArray(series) ? series.reduce((a, b) => (a || 0) + (b || 0), 0) : 0;          
          const reqData = enhancedAppointmentTypeMix?.serviceTypes?.[seriesIndex] || {};
          return (
            '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; background-color: #EBEBEB; color: #000; padding: 8px; margin: 0;">' +
            (label || 'Unknown') +
            '<span class="apexcharts-tooltip-marker" style="background-color: ' +
            (w?.globals?.colors?.[seriesIndex] || '#1D4ED8') +
            '; margin-left: 5px;"></span>' +
            "</div>" +
            '<div class="apexcharts-tooltip-series-group" style="order: 1; display: flex; background-color: #fff; padding: 8px;">' +
            '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; color: #000;">' +
            '<div class="apexcharts-tooltip-y-group">' +
            '<span class="apexcharts-tooltip-text-y-label">Total: </span>' +
            '<span class="apexcharts-tooltip-text-y-value">' +
            Number(reqData?.volumePercentage ?? 0)?.toFixed(2) + "% (" + (value || 0) + ")" +
            "</span>" +
            "</div>" +           
            '<div class="apexcharts-tooltip-y-group">' +
            '<span class="apexcharts-tooltip-text-y-label">Cancellation: </span>' +
            '<span class="apexcharts-tooltip-text-y-value">' +
            Number(reqData?.cancellationPercentage ?? 0)?.toFixed(2) +
            "%</span>" +
            "</div>" +
            '<div class="apexcharts-tooltip-y-group">' +
            '<span class="apexcharts-tooltip-text-y-label">No Show: </span>' +
            '<span class="apexcharts-tooltip-text-y-value">' +
            Number(reqData?.noShow ?? 0)?.toFixed(2) +
            "%</span>" +
            "</div>" +
            '<div class="apexcharts-tooltip-y-group">' +
            '<span class="apexcharts-tooltip-text-y-label">Revenue contribution: </span>' +
            '<span class="apexcharts-tooltip-text-y-value">' +
            Number(reqData?.revenuePercentage ?? 0)?.toFixed(2) +
            "%</span>" +
            "</div>" +
            "</div>" +
            "</div>"
          );
        } catch (error) {
          return '<div>Error loading tooltip</div>';
        }
      },
    },
  };

  // Bar Chart
  const barColors = {
    noShows: "#EF4444",
    late: "#FACC15",
    resched: "#FFB71B",
  };

  // Box Plot Chart
  const boxOptions = {
    chart: {
      type: "boxPlot",
      toolbar: { show: false },
    },
    xaxis: {
      categories: Array.isArray(boxPlotSeries) && boxPlotSeries[0]?.data ? boxPlotSeries[0].data.map((d) => d?.x || '') : [],
    },
  };

  const ageOptions = {
    chart: { type: "bar", stacked: true, toolbar: { show: false } },
    plotOptions: {
      bar: { horizontal: false, columnWidth: "40%", borderRadius: 10 },
    },
    dataLabels: {
      enabled: true,
      style: { fontSize: "13px", fontWeight: "bold", colors: ["#fff"] },
    },
    colors: ["#1D4ED8", "#9333EA"],
    xaxis: { categories: noShowCategories || [] },
    legend: { position: "top", horizontalAlign: "left" },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        try {
          const ageCategory = noShowCategories?.[dataPointIndex] || 'Unknown';
          const value = series?.[seriesIndex]?.[dataPointIndex] ?? 0;
          const hoverData = enhancedNoShowRescheduleRate?.trendData?.[dataPointIndex] || {};
          const label = seriesIndex === 0 ? "No Show" : "Reschedule";

          return (
            '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; background-color: #EBEBEB; color: #000; padding: 8px; margin: 0;">' +
            (ageCategory || 'Unknown') +
            '<span class="apexcharts-tooltip-marker" style="background-color: ' +
            (w?.globals?.colors?.[seriesIndex] || '#1D4ED8') +
            '; margin-left: 5px;"></span>' +
            "</div>" +
            '<div class="apexcharts-tooltip-series-group" style="order: 1; display: flex; background-color: #fff; padding: 8px;">' +
            '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; color: #000;">' +
            '<div class="apexcharts-tooltip-y-group">' +
            `<span class="apexcharts-tooltip-text-y-label">${label}: </span>` +
            '<span class="apexcharts-tooltip-text-y-value">' +
            (value || 0) +
            "</span>" +
            "</div>" +
            '<div class="apexcharts-tooltip-y-group">' +
            '<span class="apexcharts-tooltip-text-y-label">Total: </span>' +
            '<span class="apexcharts-tooltip-text-y-value">' +
            (hoverData?.totalAppointments ?? 0) +
            "</span>" +
            "</div>" +
            "</div>" +
            "</div>"
          );
        } catch (error) {
          return '<div>Error loading tooltip</div>';
        }
      },
    },
  };

  const ageSeriesForHover = useMemo(() => {
    if (!enhancedNoShowRescheduleRate?.trendData) return [];

    const speciesData = enhancedNoShowRescheduleRate?.trendData;
    const categories = Object.keys(speciesData);
    const reqData = categories?.map((li) => {
      const suCategories = Object.keys(speciesData[li]);
      const reqSubCatData = suCategories?.map((sc) => {
        return {
          ...speciesData[li][sc],
          count:
            (speciesData?.[li]?.[sc]?.spayed ?? 0) +
            (speciesData?.[li]?.[sc]?.neutered ?? 0) +
            (speciesData?.[li]?.[sc]?.intact ?? 0),
        };
      });
      return { name: li, data: reqSubCatData };
    });
    return reqData;
  }, [enhancedNoShowRescheduleRate]);

  return (
    <div className="dashboard-container">
      {/* Type of Appointment (Donut Chart) */}
      <div className="donut-box">
        <div className="donut-header">
          <h3>Type of Appointment Mix</h3>
          {/* <select>
            <option>Feb</option>
            <option>Mar</option>
            <option>Apr</option>
          </select> */}
        </div>
        {/* <Chart options={donutOptions} series={donutSeries} type="donut" height={250} /> */}
        {Array.isArray(donutSeries) && donutSeries.length > 0 && Array.isArray(donutLabels) && donutLabels.length > 0 ? (
          <Chart
            options={donutOptions}
            series={donutSeries}
            type="donut"
            height={250}
          />
        ) : (
          <p>No data available for appointment types.</p>
        )}
        <ul className="donut-legend">
          {Array.isArray(donutLabels) && donutLabels.map((label, index) => (
            <li key={index}>
              <span
                className={`dot dot${index + 1}`}
                style={{ backgroundColor: donutOptions.colors?.[index] || '#1D4ED8' }}
              />
              {label || 'Unknown'} - {donutSeries?.[index] ?? 0}
            </li>
          ))}
        </ul>
      </div>

      {/* Right Section */}
      <div className="right-section">
        {/* No-show / Rescheduling Rate (Bar Chart) */}
        <div className="bar-box">
          <div className="bar-header">
            <h3>No-show & Reschedule Rate</h3>
            {/* <ToggleButtonGroup
              value={view}
              exclusive
              onChange={(_, val) => val && setView(val)}
              size="small"
              sx={{
                backgroundColor: "#f0f2f5",
                borderRadius: "12px",
                "& .MuiToggleButtonGroup-grouped": {
                  borderRadius: "7px",
                  borderColor: "transparent",
                  color: "#333",
                  fontWeight: 500,
                  "&.Mui-selected": {
                    backgroundColor: "#005EB8",
                    color: "#fff",
                  },
                },
              }}
            >
              <ToggleButton value="Weekly">Week</ToggleButton>
              <ToggleButton value="Monthly">Month</ToggleButton>
              <ToggleButton value="Yearly">Year</ToggleButton>
            </ToggleButtonGroup> */}
          </div>
          {/* <ResponsiveContainer width="100%" height={250}>
            <BarChart data={barData}>
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="noShows" fill={barColors.noShows} name="No-shows" barSize={20} radius={[10, 10, 0, 0]} />
              <Bar dataKey="late" fill={barColors.late} name="Late arrivals" barSize={20} radius={[10, 10, 0, 0]} />
              <Bar dataKey="resched" fill={barColors.resched} name="Rescheduled" barSize={20} radius={[10, 10, 0, 0]} />
            </BarChart>
          </ResponsiveContainer> */}
          {ageSeries && ageSeries.length > 0 && noShowCategories && noShowCategories.length > 0 ? (
            <ReactApexChart
              options={ageOptions}
              series={ageSeries}
              type="bar"
              height={300}
            />
          ) : (
            <p>No data available for No-show & Reschedule Rate.</p>
          )}
          {/* {barData?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={barData}>
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="noShows"
                  fill={barColors.noShows}
                  name="No-shows"
                  barSize={20}
                  radius={[10, 10, 0, 0]}
                />
                <Bar
                  dataKey="late"
                  fill={barColors.late}
                  name="Late arrivals"
                  barSize={20}
                  radius={[10, 10, 0, 0]}
                />
                <Bar
                  dataKey="resched"
                  fill={barColors.resched}
                  name="Rescheduled"
                  barSize={20}
                  radius={[10, 10, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p>No data available for rescheduling rates.</p>
          )} */}
        </div>

        {/* Patient Wait Time (Box Plot) */}
        <div className="boxplot-box">
          <div className="box-header">
            <h3>Patient Wait Time</h3>
            <select>
              <option>2024</option>
              <option>2023</option>
            </select>
          </div>
          {/* <Chart options={boxOptions} series={boxPlotSeries} type="boxPlot" height={250} /> */}
          {boxPlotSeries?.length > 0 && boxPlotSeries?.[0]?.data?.length > 0 ? (
            <Chart
              options={boxOptions}
              series={boxPlotSeries}
              type="boxPlot"
              height={250}
            />
          ) : (
            <p>No data available for patient wait times.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default AppointType;
